import { tracked } from "@glimmer/tracking";
import { warn } from "@ember/debug";
import EmberObject from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { getOwner, setOwner } from "@ember/owner";
import { Promise } from "rsvp";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import {
  applyModelCallbacks,
  applyRegisteredFields,
  mergeSaveProperties,
  modelNameFor,
} from "discourse/lib/model-extensions";
import { enumerateTrackedEntries } from "discourse/lib/tracked-tools";

function waitForCallbacks(pending, next) {
  return pending ? pending.then(next) : next();
}

export default class RestModel extends EmberObject {
  // Overwrite and JSON will be passed through here before `create` and `update`
  static munge(json) {
    return json;
  }

  static create(args) {
    args = args || {};

    args.__munge = this.munge;
    const createArgs = this.munge(
      { ...args, ...Object.fromEntries(enumerateTrackedEntries(args)) },
      args.store
    );

    // Some Discourse code calls `model.create()` directly without going through the
    // store. In that case the owner is not set, and injections will fail. This workaround ensures
    // the owner is always present. Eventually we should use the store for everything to fix this.
    const receivedOwner = getOwner(createArgs);
    if (!receivedOwner || receivedOwner.isDestroyed) {
      setOwner(createArgs, getOwnerWithFallback());
    }

    return super.create(createArgs);
  }

  @tracked isSaving = false;

  primaryKey = "id";
  @tracked __state;

  constructor() {
    super(...arguments);

    // Defines plugin-registered fields as tracked properties, before the server
    // payload is assigned. See `addModelField`.
    applyRegisteredFields(this);
  }

  init() {
    super.init(...arguments);

    // Fires `init` callbacks after the create args are assigned, so they see
    // the initial state. See `addModelCallback`.
    applyModelCallbacks(modelNameFor(this), "init", this);
  }

  @dependentKeyCompat
  get isNew() {
    return this.__state === "new";
  }

  @dependentKeyCompat
  get isCreated() {
    return this.__state === "created";
  }

  beforeCreate() {}
  afterCreate() {}

  beforeUpdate() {}
  afterUpdate() {}

  update(props) {
    if (this.isSaving) {
      return Promise.reject();
    }

    props = props || this.updateProperties();

    const modelName = modelNameFor(this);
    mergeSaveProperties(modelName, this, props);

    this.beforeUpdate(props);

    this.set("isSaving", true);
    return waitForCallbacks(
      applyModelCallbacks(modelName, "beforeUpdate", this, props),
      () => this.store.update(this.__type, this.get(this.primaryKey), props)
    )
      .then((res) => {
        const payload = this.__munge(res.payload || res.responseJson);

        if (payload.success === "OK") {
          warn("An update call should return the updated attributes", {
            id: "discourse.rest-model.update-attributes",
          });
          res = props;
        }

        this.setProperties(payload);
        this.afterUpdate(res);
        return waitForCallbacks(
          applyModelCallbacks(modelName, "afterUpdate", this, res),
          () => {
            res.target = this;
            return res;
          }
        );
      })
      .finally(() => this.set("isSaving", false));
  }

  createProperties() {
    throw new Error(
      "You must overwrite `createProperties()` before saving a record"
    );
  }

  save(props) {
    return this.isNew ? this._saveNew(props) : this.update(props);
  }

  destroyRecord() {
    return this.store.destroyRecord(this.__type, this);
  }

  _saveNew(props) {
    if (this.isSaving) {
      return Promise.reject();
    }

    props = props || this.createProperties();

    const modelName = modelNameFor(this);
    mergeSaveProperties(modelName, this, props);

    this.beforeCreate(props);

    const adapter = this.store.adapterFor(this.__type);

    this.set("isSaving", true);
    return waitForCallbacks(
      applyModelCallbacks(modelName, "beforeCreate", this, props),
      () => adapter.createRecord(this.store, this.__type, props)
    )
      .then((res) => {
        if (!res) {
          throw new Error("Received no data back from createRecord");
        }

        // We can get a response back without properties, for example
        // when a post is queued.
        if (res.payload) {
          this.setProperties(this.__munge(res.payload));
          this.set("__state", "created");
        }

        this.afterCreate(res);
        return waitForCallbacks(
          applyModelCallbacks(modelName, "afterCreate", this, res),
          () => {
            res.target = this;
            return res;
          }
        );
      })
      .finally(() => this.set("isSaving", false));
  }
}
